import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InputBase, styled } from '@mui/material';
import { flowRight } from 'lodash';
import MaskedInput from 'react-text-mask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import withRequired from 'components/WithRequired/withRequired';
import withTooltip from 'components/WithTooltip/withTooltip';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { gridShortDate, isValidDate, slashFormat, TEXT_MASK_DATE_FORMAT } from 'utilities';

interface TextMaskDateProps {
  inputRef: (ref: HTMLInputElement | null) => void;
  [key: string]: any;
}

// Styled components
const ValueViewer = styled('div')({
  cursor: 'pointer',
  width: '100%',
  height: '100%',
});

const ValueContainer = styled('div')({
  position: 'relative',
  height: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'end',
});

const StyledInputBase = styled(InputBase)<{ hasError: boolean }>(({ hasError }) => ({
  '& > input': {
    fontFamily: '"Open Sans", sans-serif, arial !important',
    padding: '6px 0',
    textAlign: 'right !important',
  },
  ...(hasError && {
    '& .MuiInputBase-input': {
      color: 'red',
      borderColor: 'red',
    },
  }),
}));

export const TextMaskDate: React.FC<TextMaskDateProps> = props => {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...other}
      ref={(ref: any) => {
        inputRef(ref ? ref.inputElement : null);
      }}
      mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
      showMask
      guide
      keepCharPositions
      pipe={createAutoCorrectedDatePipe(TEXT_MASK_DATE_FORMAT, {
        minYear: 1900,
      })}
    />
  );
};

interface GridDateEditorProps {
  cell: ICell;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

const GridDateEditor = ({ cell, onChange, onKeyDown }: GridDateEditorProps) => {
  const [error, setError] = useState<boolean>(false);
  const [date, setDate] = useState<string | undefined>(
    cell.value ? slashFormat(cell.value as string) : (cell.value as string | undefined)
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [cell]);

  useEffect(() => {
    if (date && isValidDate(date)) {
      setError(false);
      onChange(gridShortDate(date));
    } else {
      setError(true);
    }
  }, [date, onChange]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const dateParam = event.target.value;
    setDate(dateParam);
  }, []);

  const handleFocus = useCallback(() => {
    if (!date && inputRef.current) {
      inputRef.current.setSelectionRange(0, 0);
    }
  }, [date]);

  const handleInputRef = useCallback((ref: HTMLInputElement | null) => {
    if (inputRef.current !== ref) {
      (inputRef as any).current = ref;
    }
  }, []);

  if (cell.hidden) {
    return null;
  }

  return (
    <ValueViewer>
      <ValueContainer>
        <StyledInputBase
          id={`${cell.key}-date_editor`}
          value={date || ''}
          inputComponent={TextMaskDate as any}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onFocus={handleFocus}
          hasError={error}
          inputProps={{
            inputRef: handleInputRef,
          }}
        />
      </ValueContainer>
    </ValueViewer>
  );
};

const enhance = flowRight(withTooltip, withRequired);

export default enhance(GridDateEditor);